import * as ReactDOM from "react-dom/client";
import * as React from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Terms } from "./payments/Terms";
import PaymentButton from "../src/UI/PaymentButton";
import Alert from "../src/UI/Alerts/Alert";
import { useTermsAgreed } from "./payments/useTermsAgreed";
import Bugsnag from "@bugsnag/js";
import { i18n } from "../src/i18n";

document.addEventListener("turbo:load", init);
document.addEventListener("turbo:frame-load", init);

function init(event) {
  const node = event.target.querySelector("#consultationPayment");

  if (!node) {
    return;
  }

  const stripePromise = loadStripe(node.dataset.stripePublishableKey);
  const clientSecret = node.dataset.stripeClientSecret;
  const returnUrl = node.dataset.returnUrl;
  const cost = node.dataset.cost;
  const options = { clientSecret };
  const root = ReactDOM.createRoot(node);

  root.render(
    <>
      <Elements options={options} stripe={stripePromise}>
        <CheckoutForm returnUrl={returnUrl} cost={cost} />
      </Elements>
    </>
  );
}

interface CheckoutFormProps {
  returnUrl: string;
  cost: number;
}

function CheckoutForm({ returnUrl, cost }: CheckoutFormProps) {
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const terms = useTermsAgreed();

  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    if (!terms.allAgreed || !stripe || !elements) {
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    });

    if (error.type === "card_error" || error.type === "validation_error") {
      setError(error.message || null);
      if (error.message) {
        Bugsnag.notify(error.message);
      }
    } else {
      setError("An unexpected error occurred.");
    }

    setIsLoading(false);
  };

  return (
    <form id="payment-form" className="mb-4" onSubmit={handleSubmit}>
      <PaymentElement id="payment-element" />
      <div className="mb-4 mt-6">
        <Terms
          id="stripe"
          onTermsChange={terms.setConditions}
          onPolicyChange={terms.setPrivacyPolicy}
        />
      </div>

      <PaymentButton
        id="submit"
        className="mb-4"
        loading={isLoading || !stripe || !elements}
      >
        Pay {i18n.localize("currency", cost)}
      </PaymentButton>

      {error && <Alert type="error">{error}</Alert>}
    </form>
  );
}
